import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import type { AlarmplanEntry, AlarmplanCategory, AlarmplanOrgEntries } from '../types';
import { Lock, Plus, Trash2, Download, Radio, FileText, Save, X, Edit3, AlertTriangle, Mail, Info, Printer, ChevronDown, Search, Check } from 'lucide-react';

const CATEGORY_LABELS: Record<string, string> = {
  FG_1: 'Führungsgrundgebiet 1', FG_2: 'Führungsgrundgebiet 2', FG_3: 'Führungsgrundgebiet 3',
  FG_4: 'Führungsgrundgebiet 4', FG_5: 'Führungsgrundgebiet 5', FG_6: 'Führungsgrundgebiet 6',
  FG_7: 'Führungsgrundgebiet 7', CDS: 'Chef des Stabes', LRK: 'Landesrettungskommandant',
  SPOC: 'Single Point of Contact', ALARMNUMMER: 'Alarmnummer', ALARMKONTAKT: 'Alarmkontakt',
};
const CATEGORY_ORDER = ['ALARMNUMMER','ALARMKONTAKT','SPOC','LRK','CDS','FG_1','FG_2','FG_3','FG_4','FG_5','FG_6','FG_7'];

const emptyEntry = (cat: string, orgName?: string): AlarmplanEntry => ({
  id: '', category: cat as AlarmplanCategory, position: 0,
  name: (cat === 'ALARMNUMMER' || cat === 'ALARMKONTAKT') ? (orgName || '') : '',
  telefon: '', handy: '', tetraIssi: '', satellit: '', email: '', info: '',
});

function asArray(val: string | string[] | undefined | null): string[] {
  if (!val) return [];
  return Array.isArray(val) ? val : val ? [val] : [];
}

function displayVal(val: string | string[] | undefined | null): string {
  if (!val) return '';
  return Array.isArray(val) ? val.join(', ') : val;
}

function toApi(val: string[] | undefined): string | undefined {
  if (!val || val.length === 0) return undefined;
  if (val.length === 1) return val[0];
  return JSON.stringify(val);
}


// ─── TETRA SHORT NAME HELPERS ─────────────────────
const STATE_ABBREV: Record<string, string> = {
  'Landesverband Wien': 'W',
  'Landesverband Niederösterreich': 'Noe',
  'Landesverband Oberösterreich': 'Ooe',
  'Landesverband Steiermark': 'STM',
  'Landesverband Salzburg': 'Sbg',
  'Landesverband Tirol': 'T',
  'Landesverband Vorarlberg': 'V',
  'Landesverband Burgenland': 'Bgld',
  'Landesverband Kärnten': 'Ktn',
};

function findStateAbbrev(orgId: string, allOrgs: any[]): string | null {
  const findOrg = (id: string): any => allOrgs.find(o => o.id === id);
  let current = findOrg(orgId);
  while (current) {
    if (STATE_ABBREV[current.name]) return STATE_ABBREV[current.name];
    if (current.parentId) {
      current = findOrg(current.parentId);
    } else {
      break;
    }
  }
  return null;
}

function tetraContactName(name: string, category: string, stateAbbrev: string | null): string {
  if (stateAbbrev && (category === 'FG_1' || category === 'FG_2' || category === 'FG_3' || 
      category === 'FG_4' || category === 'FG_5' || category === 'FG_6' || category === 'FG_7')) {
    return `${stateAbbrev} S${category.replace('FG_', '')}`;
  }
  return name.substring(0, 32);
}

// ─── SEARCHABLE MULTI-SELECT DROPDOWN ─────────────

function OrgMultiSelect({
  orgs,
  selected,
  onChange,
  label,
}: {
  orgs: { id: string; name: string }[];
  selected: Set<string>;
  onChange: (ids: Set<string>) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = orgs.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.id.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const selectAll = () => {
    onChange(new Set(orgs.map(o => o.id)));
  };

  const selectNone = () => {
    onChange(new Set());
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50"
      >
        <span className="truncate">
          {selected.size === 0
            ? 'Keine Organisation ausgewählt'
            : selected.size === 1
              ? orgs.find(o => o.id === Array.from(selected)[0])?.name || '1 Organisation'
              : `${selected.size} Organisationen ausgewählt`}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 flex flex-col">
          <div className="flex items-center gap-1 p-2 border-b border-gray-100">
            <Search size={14} className="text-gray-400 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-sm outline-none border-none bg-transparent"
              placeholder="Organisation suchen..."
              autoFocus
            />
          </div>
          <div className="flex gap-2 px-2 py-1 border-b border-gray-100">
            <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-800">Alle</button>
            <button onClick={selectNone} className="text-xs text-gray-500 hover:text-gray-700">Keine</button>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-gray-400 text-center">Keine Organisationen gefunden</div>
            ) : (
              filtered.map(o => {
                const isSel = selected.has(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggle(o.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left border-l-2 transition-colors ${
                      isSel
                        ? 'bg-red-50 border-l-red-500 text-red-800 font-medium'
                        : 'border-l-transparent text-gray-700 hover:bg-gray-50 hover:border-l-gray-300'
                    }`}
                  >
                    <span className="truncate">{o.name}</span>
                    {isSel && <Check size={14} className="shrink-0 text-red-500" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────

export default function AlarmplanPage() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<AlarmplanEntry | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [orgData, setOrgData] = useState<AlarmplanOrgEntries[]>([]);
  const [activeOrgId, setActiveOrgId] = useState(user?.organizationId || '');
  const [orgs, setOrgs] = useState<any[]>([]);
  const [keySetupMode, setKeySetupMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [setupForOrgId, setSetupForOrgId] = useState('');
  const [lockMessage, setLockMessage] = useState('');
  const [accessibleOrgIds, setAccessibleOrgIds] = useState<string[]>([]);
  const [selectedViewOrgs, setSelectedViewOrgs] = useState<Set<string>>(new Set());
  const [selectedExportOrgs, setSelectedExportOrgs] = useState<Set<string>>(new Set());
  const [exportMode, setExportMode] = useState<'none' | 'pdf' | 'vcf'>('none');
  const [baudRate, setBaudRate] = useState(115200);

  const [multiFields, setMultiFields] = useState<Record<string, string[]>>({
    telefon: [], handy: [], tetraIssi: [], satellit: [], email: [],
  });

  useEffect(() => {
    api.get('/organizations').then(r => {
      setOrgs(r.data || []);
      if (!activeOrgId && r.data?.[0]) setActiveOrgId(r.data[0].id);
    }).catch(() => {});
  }, []);

  // Load accessible orgs for view selector + export
  useEffect(() => {
    if (activeOrgId) {
      api.get(`/alarmplan/${activeOrgId}/accessible-orgs`).then(r => {
        const ids = r.data || [];
        setAccessibleOrgIds(ids);
        // Default: select all accessible orgs for viewing
        if (selectedViewOrgs.size === 0) setSelectedViewOrgs(new Set(ids));
        setSelectedExportOrgs(new Set(ids));
      }).catch(() => {});
    }
  }, [activeOrgId]);

  const checkStatus = useCallback(async () => {
    try {
      const r = await api.get(`/alarmplan/${activeOrgId}/status`);
      setUnlocked(r.data.unlocked);
    } catch { setUnlocked(false); }
  }, [activeOrgId]);

  useEffect(() => { if (activeOrgId) checkStatus(); }, [activeOrgId, checkStatus]);

  const loadEntries = useCallback(async () => {
    setLoading(true); setError('');
    try {
      if (selectedViewOrgs.size === 0) {
        setOrgData([]);
        setLoading(false);
        return;
      }
      const orgIds = Array.from(selectedViewOrgs);
      const r = await api.post(`/alarmplan/${activeOrgId}/entries/multi`, { orgIds });
      setOrgData(r.data || []);
      setUnlocked(true);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Fehler beim Laden');
    }
    setLoading(false);
  }, [activeOrgId, selectedViewOrgs]);

  // Reload when selected orgs change
  useEffect(() => {
    if (unlocked && selectedViewOrgs.size > 0) loadEntries();
  }, [selectedViewOrgs]);

  const handleUnlock = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.post(`/alarmplan/${activeOrgId}/unlock`, { password });
      if (r.data.success) { loadEntries(); }
      else { setError('Falsches Passwort'); }
    } catch (e: any) { setError(e.response?.data?.message || e.message || 'Fehler beim Entsperren'); }
    setPassword(''); setLoading(false);
  };

  const handleLock = async () => {
    await api.post(`/alarmplan/${activeOrgId}/lock`);
    setUnlocked(false); setOrgData([]);
  };

  const canManageKeys = !!user?.role?.startsWith('ADMIN_');

  const handleSetPassword = async () => {
    if (newPassword !== confirmPassword) { setError('Passwörter stimmen nicht überein'); return; }
    if (newPassword.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    setLoading(true); setError('');
    try {
      const orgId = setupForOrgId || activeOrgId;
      await api.post(`/alarmplan/${orgId}/password`, { password: newPassword });
      setKeySetupMode(false); setNewPassword(''); setConfirmPassword('');
      setLockMessage('Passwort erfolgreich gesetzt!');
      setTimeout(() => setLockMessage(''), 3000);
    } catch (e: any) { setError(e.response?.data?.message || 'Fehler beim Setzen des Passworts'); }
    setLoading(false);
  };

  const initMultiFields = (entry: AlarmplanEntry) => {
    setMultiFields({
      telefon: asArray(entry.telefon),
      handy: asArray(entry.handy),
      tetraIssi: asArray(entry.tetraIssi),
      satellit: asArray(entry.satellit),
      email: asArray(entry.email),
    });
  };

  const addField = (field: string) => {
    setMultiFields(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  };

  const removeField = (field: string, idx: number) => {
    setMultiFields(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== idx),
    }));
  };

  const setFieldVal = (field: string, idx: number, val: string) => {
    setMultiFields(prev => {
      const arr = [...(prev[field] || [])];
      arr[idx] = val;
      return { ...prev, [field]: arr };
    });
  };

  const buildDto = (entry: AlarmplanEntry) => ({
    category: entry.category,
    position: entry.position,
    name: entry.name,
    telefon: toApi(multiFields.telefon),
    handy: toApi(multiFields.handy),
    tetraIssi: toApi(multiFields.tetraIssi),
    satellit: toApi(multiFields.satellit),
    email: toApi(multiFields.email),
    info: entry.info || undefined,
  });

  const handleCreate = async () => {
    if (!editing) return;
    if (!editing.name && editing.category !== 'ALARMNUMMER' && editing.category !== 'ALARMKONTAKT') return;
    setLoading(true);
    try {
      await api.post(`/alarmplan/${activeOrgId}/entries`, buildDto(editing));
      setEditing(null); loadEntries();
    } catch (e: any) { setError(e.response?.data?.message || 'Fehler beim Erstellen'); }
    setLoading(false);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    setLoading(true);
    try {
      await api.put(`/alarmplan/${activeOrgId}/entries/${editing.id}`, buildDto(editing));
      setEditing(null); loadEntries();
    } catch (e: any) { setError(e.response?.data?.message || 'Fehler beim Aktualisieren'); }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eintrag wirklich löschen?')) return;
    setLoading(true);
    try { await api.delete(`/alarmplan/${activeOrgId}/entries/${id}`); loadEntries(); }
    catch (e: any) { setError(e.response?.data?.message || 'Fehler beim Löschen'); }
    setLoading(false);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} Einträge wirklich löschen?`)) return;
    setLoading(true);
    try {
      await api.post(`/alarmplan/${activeOrgId}/entries/bulk-delete`, { ids: Array.from(selectedIds) });
      setSelectedIds(new Set()); loadEntries();
    } catch (e: any) { setError(e.response?.data?.message || 'Fehler beim Löschen'); }
    setLoading(false);
  };

  // ─── EXPORTS ────────────────────────────────────

  const handlePdfExport = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/alarmplan/${activeOrgId}/export/pdf`, { orgIds: Array.from(selectedExportOrgs) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'alarmplan.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) { setError('PDF Export fehlgeschlagen'); }
    setLoading(false);
  };

  const handleVcfExport = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/alarmplan/${activeOrgId}/export/vcf`, { orgIds: Array.from(selectedExportOrgs) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/vcard' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'alarmplan_kontakte.vcf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) { setError('vCard Export fehlgeschlagen'); }
    setLoading(false);
  };

  const handleTetraCsvExport = async () => {
    try {
      const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
      let contacts = orgData.flatMap((o: any) => o.entries || []);
      if (ids) contacts = contacts.filter((c: any) => ids.includes(c.id));
      const tetraContacts = contacts.filter((c: any) => asArray(c.tetraIssi).length > 0);

      const header = 'name;number;type';
      const rows: string[] = [header];
      const stateAbbrev = findStateAbbrev(activeOrgId, orgs);
      for (const e of tetraContacts) {
        const contactName = tetraContactName(e.name, e.category, stateAbbrev);
        const issis = asArray(e.tetraIssi);
        issis.forEach((issi: string) => rows.push(`${contactName};${issi};issi`));
        const phones = asArray(e.telefon);
        phones.forEach((p: string) => rows.push(`${contactName};${p};phone`));
        const mobiles = asArray(e.handy);
        mobiles.forEach((m: string) => rows.push(`${contactName};${m};mobil`));
        const sats = asArray(e.satellit);
        sats.forEach((s: string) => rows.push(`${contactName};${s};phone`));
      }

      const csv = rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'tetra_contacts.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError('Fehler beim TETRA CSV-Export');
    }
  };

  const hasWebSerial = !!(navigator as any).serial;

  const handleTetraWebSerial = async () => {
    if (!hasWebSerial) {
      await handleTetraCsvExport();
      return;
    }
    try {
      const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
      let contacts = orgData.flatMap((o: any) => o.entries || []);
      if (ids) contacts = contacts.filter((c: any) => ids.includes(c.id));
      const tetraContacts = contacts.filter((c: any) => {
        const issis = asArray(c.tetraIssi);
        return issis.length > 0;
      });

      const port = await (navigator as any).serial.requestPort();
      await port.open({ baudRate });
      const writer = port.writable!.getWriter();
      const enc = new TextEncoder();

      await writer.write(enc.encode('AT\r\n'));
      await new Promise(r => setTimeout(r, 300));
      await writer.write(enc.encode('AT+CPBS="ME"\r\n'));
      await new Promise(r => setTimeout(r, 300));

      const stateAbbrev = findStateAbbrev(activeOrgId, orgs);
      for (const c of tetraContacts) {
        const issis = asArray(c.tetraIssi);
        for (const issi of issis) {
          const contactName = tetraContactName(c.name, c.category, stateAbbrev);
          const cmd = `AT+CPBW=,${issi},129,"${contactName}"\r\n`;
          await writer.write(enc.encode(cmd));
          await new Promise(r => setTimeout(r, 500));
        }
      }

      writer.releaseLock();
      await port.close();
      setLockMessage(`${tetraContacts.length} TETRA-Kontakte in HFG-Kontaktliste gespeichert!`);
      setTimeout(() => setLockMessage(''), 5000);
    } catch (e: any) {
      if (e.name === 'NotFoundError') {
        await handleTetraCsvExport();
      } else { setError(e.message || 'Fehler bei der TETRA-Programmierung'); }
    }
  };

  // ─── RENDER HELPERS ────────────────────────────

  const renderFieldMulti = (label: string, field: string, icon?: React.ReactNode, placeholderText?: string) => {
    const values = multiFields[field] || [];
    return (
      <div>
        <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">{icon} {label}</label>
        {values.map((v, i) => (
          <div key={i} className="flex gap-1 mb-1">
            <input value={v} onChange={e => setFieldVal(field, i, e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" placeholder={placeholderText || label} />
            {values.length > 1 && (
              <button type="button" onClick={() => removeField(field, i)}
                className="p-1 text-red-400 hover:text-red-600"><X size={14} /></button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => addField(field)}
          className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1">
          <Plus size={12} /> Weitere {label}
        </button>
      </div>
    );
  };

  const renderFieldValue = (val: string | string[] | undefined | null): string => {
    if (!val) return '';
    const arr = asArray(val);
    return arr.join(', ');
  };

  // Build full org objects for the multi-select from accessible IDs
  const accessibleOrgsFull = accessibleOrgIds
    .map(id => {
      const o = orgs.find((o: any) => o.id === id);
      return { id, name: o?.name || id };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // ─── RENDER ────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Alarmplan</h1>
        <div className="flex items-center gap-2">
          {canManageKeys && !unlocked && (
            <button onClick={() => { setKeySetupMode(!keySetupMode); setSetupForOrgId(activeOrgId); }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg hover:bg-yellow-100">
              <Lock size={14} /> Passwort setzen
            </button>
          )}
          {unlocked && (
            <button onClick={handleLock} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100">
              <Lock size={14} /> Sperren
            </button>
          )}
        </div>
      </div>

      {lockMessage && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{lockMessage}</div>}
      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {keySetupMode && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
          <h3 className="font-semibold text-yellow-800">Alarmplan-Passwort setzen</h3>
          <p className="text-sm text-yellow-700">Dieses Passwort wird benötigt, um den Alarmplan zu entsperren.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-yellow-700 mb-1">Organisation</label>
              <select value={setupForOrgId} onChange={e => setSetupForOrgId(e.target.value)} className="w-full border border-yellow-300 rounded px-2 py-1.5 text-sm">
                {orgs.filter((o: any) => true).map((o: any) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-yellow-700 mb-1">Neues Passwort</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full border border-yellow-300 rounded px-2 py-1.5 text-sm" placeholder="mind. 8 Zeichen" />
            </div>
            <div>
              <label className="block text-xs text-yellow-700 mb-1">Passwort bestätigen</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full border border-yellow-300 rounded px-2 py-1.5 text-sm" placeholder="Wiederholung" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSetPassword} disabled={loading} className="px-4 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm">Speichern</button>
            <button onClick={() => { setKeySetupMode(false); setNewPassword(''); setConfirmPassword(''); }} className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">Abbrechen</button>
          </div>
        </div>
      )}

      {!unlocked && !keySetupMode && (
        <div className="max-w-md mx-auto mt-8 p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="text-center mb-4">
            <Lock size={32} className="mx-auto text-gray-400 mb-2" />
            <h2 className="text-lg font-semibold text-gray-700">Alarmplan gesperrt</h2>
            <p className="text-sm text-gray-500 mt-1">Gib das Alarmplan-Passwort deiner Organisation ein.</p>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Organisation</label>
              <select value={activeOrgId} onChange={e => setActiveOrgId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {orgs.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Passwort</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Alarmplan-Passwort" />
            </div>
            <button onClick={handleUnlock} disabled={loading || !password} className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium">
              {loading ? 'Entsperre...' : 'Entsperren'}
            </button>
          </div>
        </div>
      )}

      {unlocked && (
        <>
          {/* Action Bar */}
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={() => { setEditing(emptyEntry('ALARMNUMMER', orgs.find(o => o.id === activeOrgId)?.name)); setSelectedIds(new Set()); initMultiFields(emptyEntry('ALARMNUMMER')); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs">
              <Plus size={14} /> Neuer Eintrag
            </button>

            {selectedIds.size > 0 && (
              <>
                <button onClick={handleBulkDelete} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-xs">
                  <Trash2 size={14} /> {selectedIds.size} löschen
                </button>
                <button onClick={handleTetraWebSerial} className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 border border-green-200 rounded-lg hover:bg-green-100 text-xs">
                  <Radio size={14} /> {hasWebSerial ? 'In HFG-Kontaktliste speichern' : 'TETRA CSV'} ({selectedIds.size})
                </button>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-400">Baud</label>
                  <select value={baudRate} onChange={e => setBaudRate(Number(e.target.value))}
                    className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600">
                    <option value={9600}>9600</option>
                    <option value={19200}>19200</option>
                    <option value={38400}>38400</option>
                    <option value={57600}>57600</option>
                    <option value={115200}>115200</option>
                  </select>
                </div>
              </>
            )}

            <div className="flex-1" />

            <button onClick={() => setExportMode(exportMode === 'pdf' ? 'none' : 'pdf')}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg ${exportMode === 'pdf' ? 'bg-red-50 text-red-700 border-red-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
              <FileText size={14} /> PDF Export
            </button>
            <button onClick={() => setExportMode(exportMode === 'vcf' ? 'none' : 'vcf')}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg ${exportMode === 'vcf' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
              <Download size={14} /> vCard Export
            </button>
            <button onClick={handleTetraCsvExport} className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs">
              <Download size={14} /> TETRA CSV (alle)
            </button>
          </div>

          {/* ✨ ORGANISATIONS-AUSWAHL — Searchable Multi-Select ✨ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OrgMultiSelect
              orgs={accessibleOrgsFull}
              selected={selectedViewOrgs}
              onChange={setSelectedViewOrgs}
              label="Angezeigte Organisationen"
            />
            <div className="text-xs text-gray-400 self-end pb-2">
              {selectedViewOrgs.size} von {accessibleOrgsFull.length} Organisationen ausgewählt
              {loading && <span className="ml-2 text-blue-500">Lade...</span>}
            </div>
          </div>

          {/* Edit Form */}
          {editing && (
            <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-gray-700">{editing.id ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Kategorie</label>
                  <select value={editing.category} onChange={e => {
              const newCat = e.target.value;
              const isNoNameCat = (newCat === 'ALARMNUMMER' || newCat === 'ALARMKONTAKT');
              const wasNoNameCat = (editing.category === 'ALARMNUMMER' || editing.category === 'ALARMKONTAKT');
              if (isNoNameCat && !wasNoNameCat && !editing.id) {
                setEditing({...editing, category: newCat as any, name: orgs.find(o => o.id === activeOrgId)?.name || ''});
              } else if (!editing.id && wasNoNameCat && !isNoNameCat) {
                setEditing({...editing, category: newCat as any, name: ''});
              } else {
                setEditing({...editing, category: newCat as any});
              }
            }} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                    {CATEGORY_ORDER.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                  </select>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Name {editing.category !== 'ALARMNUMMER' && editing.category !== 'ALARMKONTAKT' ? '*' : ''}</label>
                  <input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    placeholder={editing.category === 'ALARMNUMMER' || editing.category === 'ALARMKONTAKT' ? 'Automatisch (Organisation)' : 'Name der Person'} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {renderFieldMulti('Telefon', 'telefon', undefined, '+43 1 xxxxxxx')}
                {renderFieldMulti('Handy', 'handy', undefined, '+43 664 xxxxxxx')}
                {renderFieldMulti('TETRA-ISSI', 'tetraIssi', undefined, '06xxxxxx')}
                {renderFieldMulti('Satellitentelefon', 'satellit', undefined, '+88216 xxxxx')}
                {renderFieldMulti('E-Mail', 'email', <Mail size={12} />)}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1"><Info size={12} /> Info (Freitext)</label>
                <textarea value={editing.info || ''} onChange={e => setEditing({...editing, info: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" rows={2} placeholder="Zusätzliche Informationen..." />
              </div>
              <div className="flex gap-2">
                <button onClick={editing.id ? handleUpdate : handleCreate} disabled={loading || (!editing.name && editing.category !== 'ALARMNUMMER' && editing.category !== 'ALARMKONTAKT')} className="flex items-center gap-1 px-4 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50">
                  <Save size={14} /> {editing.id ? 'Aktualisieren' : 'Erstellen'}
                </button>
                <button onClick={() => setEditing(null)} className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                  <X size={14} /> Abbrechen
                </button>
              </div>
            </div>
          )}

          {/* Export Org Selector */}
          {exportMode !== 'none' && (
            <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-gray-700">{exportMode === 'pdf' ? 'PDF Export' : 'vCard Export'}</h3>
              <p className="text-xs text-gray-500">Organisationen auswählen, die exportiert werden sollen:</p>
              <OrgMultiSelect
                orgs={accessibleOrgsFull}
                selected={selectedExportOrgs}
                onChange={setSelectedExportOrgs}
                label="Export-Organisationen"
              />
              <div className="flex justify-end">
                <button onClick={exportMode === 'pdf' ? handlePdfExport : handleVcfExport}
                  disabled={selectedExportOrgs.size === 0 || loading}
                  className="px-4 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs disabled:opacity-50">
                  {loading ? 'Exportiere...' : `${exportMode === 'pdf' ? 'PDF' : 'vCard'} exportieren (${selectedExportOrgs.size} Org.)`}
                </button>
              </div>
            </div>
          )}

          {/* Entry List */}
          {loading ? (
            <div className="text-center py-8 text-gray-400">Lade Alarmplan...</div>
          ) : selectedViewOrgs.size === 0 ? (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
              <AlertTriangle size={32} className="mx-auto text-gray-300 mb-2" />
              <p>Keine Organisationen ausgewählt</p>
              <p className="text-xs mt-1">Wähle oben eine oder mehrere Organisationen aus</p>
            </div>
          ) : orgData.length === 0 ? (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
              <AlertTriangle size={32} className="mx-auto text-gray-300 mb-2" />
              <p>Keine Einträge in den ausgewählten Organisationen</p>
              <p className="text-xs mt-1">Erstelle den ersten Eintrag mit "Neuer Eintrag"</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orgData.map(org => (
                <div key={org.organizationId} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
                    {org.organizationName}
                  </div>
                  {CATEGORY_ORDER.map(cat => {
                    const catEntries = org.entries.filter(e => e.category === cat);
                    if (catEntries.length === 0) return null;
                    return (
                      <div key={`${org.organizationId}-${cat}`}>
                        <div className="px-4 py-1.5 bg-red-50 border-b border-red-100 text-xs font-bold text-red-800 uppercase tracking-wider">
                          {CATEGORY_LABELS[cat]}
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-gray-500 border-b border-gray-100">
                              <th className="px-3 py-1 text-left">Name</th>
                              <th className="px-3 py-1 text-left">Telefon</th>
                              <th className="px-3 py-1 text-left">Handy</th>
                              <th className="px-3 py-1 text-left">TETRA-ISSI</th>
                              <th className="px-3 py-1 text-left">Satellit</th>
                              <th className="px-3 py-1 text-left">E-Mail</th>
                              <th className="px-3 py-1 text-left">Info</th>
                              {org.organizationId === activeOrgId && <th className="w-16 px-2 py-1"></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {catEntries.map(entry => {
                              const isSel = selectedIds.has(entry.id);
                              const ownOrg = org.organizationId === activeOrgId;
                              return (
                              <tr key={entry.id}
                                onClick={() => {
                                  const next = new Set(selectedIds);
                                  next.has(entry.id) ? next.delete(entry.id) : next.add(entry.id);
                                  setSelectedIds(next);
                                }}
                                className={`border-b border-gray-50 transition-colors cursor-pointer ${
                                  isSel
                                    ? 'bg-blue-50 hover:bg-blue-100'
                                    : 'hover:bg-gray-50'
                                }`}>
                                <td className={`px-3 py-1 font-medium ${isSel ? 'text-blue-800' : ''}`}>{entry.name || ((entry.category === 'ALARMNUMMER' || entry.category === 'ALARMKONTAKT') ? org.organizationName : '')}</td>
                                <td className="px-3 py-1">{renderFieldValue(entry.telefon)}</td>
                                <td className="px-3 py-1">{renderFieldValue(entry.handy)}</td>
                                <td className={`px-3 py-1 font-mono text-xs ${isSel ? 'text-blue-700' : ''}`}>{renderFieldValue(entry.tetraIssi)}</td>
                                <td className="px-3 py-1">{renderFieldValue(entry.satellit)}</td>
                                <td className="px-3 py-1 text-xs">{renderFieldValue(entry.email)}</td>
                                <td className="px-3 py-1 text-xs text-gray-500 max-w-[120px] truncate">{renderFieldValue(entry.info)}</td>
                                {ownOrg && (
                                  <td className="px-2 py-1" onClick={e => e.stopPropagation()}>
                                    <div className="flex gap-1">
                                      <button onClick={() => { setEditing(entry); initMultiFields(entry); }} className="p-1 text-gray-400 hover:text-blue-600" title="Bearbeiten">
                                        <Edit3 size={14} />
                                      </button>
                                      <button onClick={() => handleDelete(entry.id)} className="p-1 text-gray-400 hover:text-red-600" title="Löschen">
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </td>
                                )}
                                {!ownOrg && (
                                  <td className="px-2 py-1"></td>
                                )}
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
